'use client';

/**
 * Общая панель фильтров дашборда и отчётов.
 *
 * Подпись периода берётся из ответа (`period`), а не из выбранных полей: их
 * толкует сервер в поясе тенанта, и «сегодня» для Алматы начинается в 19:00
 * UTC вчера. Показав тут свою дату, интерфейс разошёлся бы с цифрами под ней.
 */

import React from 'react';
import { CalendarRange } from 'lucide-react';
import { flattenUnits, useEmployees, useSources, useUnits } from '@/lib/api/hooks';
import { periodLabel } from '@/lib/format';
import type { ReportFilters, ReportPeriod } from '@/lib/api/types';
import { presetRange, type PeriodPreset } from './useReportFilters';

const presets: { id: PeriodPreset; label: string }[] = [
  { id: 'today', label: 'Сегодня' },
  { id: 'week', label: '7 дней' },
  { id: 'month', label: 'Месяц' },
  { id: 'quarter', label: '90 дней' },
];

export default function ReportFilterBar({
  filters,
  patch,
  preset,
  period,
  showEmployee = false,
  showSource = true,
  extra,
  top,
}: {
  filters: ReportFilters;
  patch: (changes: Partial<ReportFilters>) => void;
  preset: PeriodPreset;
  /** Из ответа любой ручки экрана. Пока его нет — подписи периода нет. */
  period?: ReportPeriod;
  showEmployee?: boolean;
  showSource?: boolean;
  /** Разрезы конкретного экрана: groupBy, bucket, сортировка. */
  extra?: React.ReactNode;
  /**
   * Вкладки и действия. Живут внутри той же карточки, а не отдельной
   * полосой над ней: панель управления экраном — один предмет, и разорванная
   * надвое она читается как два несвязанных блока.
   */
  top?: React.ReactNode;
}) {
  const units = useUnits();
  const sources = useSources();
  const employees = useEmployees({ status: 'active' });
  const unitOptions = flattenUnits(units.data);

  return (
    <div className="card p-4 space-y-3">
      {top && (
        <div className="flex flex-wrap items-center justify-between gap-3 pb-3 border-b border-slate-100">
          {top}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex gap-1 bg-slate-100 p-1 rounded-lg">
          {presets.map((item) => (
            <button
              key={item.id}
              onClick={() => patch(presetRange(item.id))}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                preset === item.id
                  ? 'bg-white text-brand-accent shadow-sm font-semibold'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-1.5">
          <input
            type="date"
            value={filters.from ?? ''}
            onChange={(event) => patch({ from: event.target.value })}
            className="input-field py-1.5 text-xs w-36"
            title="С этой даты включительно"
          />
          <span className="text-xs text-slate-400">—</span>
          <input
            type="date"
            value={filters.to ?? ''}
            onChange={(event) => patch({ to: event.target.value })}
            className="input-field py-1.5 text-xs w-36"
            title="До этой даты, не включая её"
          />
        </div>

        {/* Что именно посчитано — говорит сервер. */}
        <span className="inline-flex items-center gap-1.5 text-xs text-slate-500">
          <CalendarRange size={13} className="flex-shrink-0" />
          {period ? (
            <>
              {periodLabel(period)}
              <span className="text-slate-400" title="Период посчитан в часовом поясе компании">
                · {period.timezone}
              </span>
            </>
          ) : (
            <span className="text-slate-400">период считается…</span>
          )}
        </span>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="w-52">
          {/* Узел трактуется поддеревом и пересекается с видимой областью. */}
          <label className="label">Подразделение</label>
          <select
            value={filters.unit ?? ''}
            onChange={(event) => patch({ unit: event.target.value })}
            className="input-field py-1.5 text-xs"
          >
            <option value="">Все подразделения</option>
            {unitOptions.map((unit) => (
              <option key={unit.id} value={unit.id}>
                {' '.repeat(unit.depth * 2)}
                {unit.name}
                {unit.status === 'archived' ? ' (архив)' : ''}
              </option>
            ))}
          </select>
        </div>

        {showSource && (
          <div className="w-44">
            <label className="label">Источник</label>
            <select
              value={filters.source ?? ''}
              onChange={(event) => patch({ source: event.target.value })}
              className="input-field py-1.5 text-xs"
            >
              <option value="">Все источники</option>
              {(sources.data ?? []).map((source) => (
                <option key={source.id} value={source.id}>
                  {source.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {showEmployee && (
          <div className="w-44">
            <label className="label">Сотрудник</label>
            <select
              value={filters.employee ?? ''}
              onChange={(event) => patch({ employee: event.target.value })}
              className="input-field py-1.5 text-xs"
            >
              <option value="">Все сотрудники</option>
              {(employees.data ?? []).map((employee) => (
                <option key={employee.id} value={employee.id}>
                  {employee.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {extra}
      </div>
    </div>
  );
}
